import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, Eye, Stars, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { useLang } from "../context/LanguageContext";
import { EXTRA } from "../content/extra";

const CERT_IMG = "/euipo-cert-1.png";

const noSave = (e) => e.preventDefault();

export const EuipoTrademark = () => {
  const { lang } = useLang();
  const e = EXTRA[lang].euipo;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (ev) => ev.key === "Escape" && setOpen(false);
    if (open) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const facts = [
    [e.regLabel, e.regNumber],
    [e.dateLabel, e.date],
    [e.typeLabel, e.type],
  ];

  return (
    <section
      id="euipo"
      data-testid="euipo-section"
      className="relative z-10 py-20 md:py-28 bg-[#050505] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute right-0 top-0 w-[420px] h-[420px] bg-[#1a3a8f]/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-5">
            <span className="h-px w-8 bg-[#D4AF37]/60" />
            {e.overline}
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">{e.title}</h2>
          <p className="text-white/60 text-lg leading-relaxed mt-7">{e.text}</p>

          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="euipo-button"
            className="group inline-flex items-center gap-2 mt-9 border border-[#D4AF37] text-[#D4AF37] px-8 py-3.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
          >
            <Eye size={17} />
            {e.button}
          </button>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative border border-white/10 bg-[#0a0a0a] p-8 md:p-10">
            <div className="absolute -top-4 -left-4 w-14 h-14 flex items-center justify-center bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black shadow-[0_10px_30px_rgba(212,175,55,0.35)]">
              <ShieldCheck size={26} strokeWidth={1.75} />
            </div>
            <div className="flex items-center gap-2 text-[#D4AF37] mb-7 pl-12">
              <Stars size={16} />
              <span className="tracking-[0.2em] text-xs uppercase">EUIPO · European Union</span>
            </div>
            <dl className="divide-y divide-white/10">
              {facts.map(([label, value], i) => (
                <div key={i} className="flex items-center justify-between gap-6 py-4">
                  <dt className="text-xs tracking-wider uppercase text-white/40">{label}</dt>
                  <dd className="font-display text-xl md:text-2xl text-[#F2E3C6] text-right">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </div>

      {/* View-only modal (portaled to body so it escapes the section stacking context) */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="euipo-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-10"
            >
              <button
                data-testid="euipo-modal-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="absolute top-5 right-5 z-[210] w-11 h-11 flex items-center justify-center rounded-full border border-white/20 bg-black/60 text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <X size={22} />
              </button>

              <motion.div
                initial={{ scale: 0.96, y: 12 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 12 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                onClick={(ev) => ev.stopPropagation()}
                className="relative max-w-3xl w-full max-h-[88vh] overflow-auto select-none"
                onContextMenu={noSave}
              >
                <img
                  src={CERT_IMG}
                  alt={e.title}
                  draggable={false}
                  onContextMenu={noSave}
                  onDragStart={noSave}
                  className="w-full h-auto rounded-sm pointer-events-none select-none"
                  style={{ WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none" }}
                />
                <div className="absolute inset-0" onContextMenu={noSave} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
};
